
    import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
    import { StockItem, Product, Table, TaxRate, CartItem, Transaction, ZReport, AppSettings, PaymentMethod, LoyaltySettings, Customer, CustomerAccountMovement, Staff, ExpenseCategory, ExpenseTransaction, ModifierGroup, Section, ShiftReport, QrMenuSettings, SubscriptionFeature, Category } from '../types';
    import { PRODUCTS, TABLES, INITIAL_STOCK, INITIAL_TAX_RATES, INITIAL_MODIFIER_GROUPS, INITIAL_SECTIONS, INITIAL_CATEGORIES, PERMISSIONS } from '../constants';
    import { getInitialData } from '../src/data/initialData';
    import { db } from '../db';
    import LoadingScreen from '../src/components/LoadingScreen';
    import { auth, db as firestore } from '../src/firebase';
    import { onIdTokenChanged, User as FirebaseUser } from 'firebase/auth';
    import { doc, getDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
    import { parseLocalStorage } from '../src/utils/storageUtils';
    import { normalizePhone } from '../src/utils/phoneUtils';
    import { getBusinessDayKey } from '../src/utils/dateUtils';
    import { useModifierGroups } from '../src/hooks/useModifierGroups';
    import { useTaxRates } from '../src/hooks/useTaxRates';
    import { useSections } from '../src/hooks/useSections';
    import { useExpenses } from '../src/hooks/useExpenses';
    import { useStaff } from '../src/hooks/useStaff';
    import { useCustomers } from '../src/hooks/useCustomers';
import { useLoyalty } from '../src/hooks/useLoyalty';
import { useStock } from '../src/hooks/useStock';
import { useProducts } from '../src/hooks/useProducts';
import { useTables } from '../src/hooks/useTables';
import { useToast } from './ToastContext';

interface DataContextType {
    stockItems: StockItem[];
    addStockItem: (item: StockItem) => void;
    updateStockItem: (item: StockItem) => void;
    deleteStockItem: (id: number) => void;
    reportWastage: (stockId: number, amount: number, reason: string) => Promise<void>;

    // QR Menu Optimization
    dirtyQrProductIds: Set<number>;
    dirtyQrCategoryIds: Set<string>;
    dirtyQrModifierGroupIds: Set<number>;
    deletedQrProductIds: Set<number>;
    deletedQrCategoryIds: Set<string>;
    deletedQrModifierGroupIds: Set<number>;
    markQrProductDeleted: (id: number) => void;
    markQrCategoryDeleted: (id: string) => void;
    markQrModifierGroupDeleted: (id: number) => void;
    clearDirtyQrLists: () => void;

    products: Product[];
    addProduct: (item: Product) => void;
    updateProduct: (item: Product) => void;
    deleteProduct: (id: number) => void;

    categories: Category[];
    addCategory: (item: Category) => void;
    updateCategory: (item: Category) => void;
    deleteCategory: (id: string) => void;

    tables: Table[];
    addTable: (item: Table) => void;
    updateTable: (item: Table) => void;
    deleteTable: (id: number) => void;
    resetTable: (id: number) => Promise<void>;
    transferTable: (sourceId: number, targetId: number) => Promise<void>;
    transferTableItems: (sourceId: number, targetId: number, itemIds: string[]) => Promise<void>; // New: Item Transfer

    sendQrOrder: (tableId: number, items: CartItem[]) => Promise<void>;
    lastNotification: { tableId: number; timestamp: number } | null;

    sections: Section[];
    addSection: (item: Section) => void;
    updateSection: (item: Section) => void;
    deleteSection: (id: number) => void;

    taxRates: TaxRate[];
    addTaxRate: (item: TaxRate) => void;
    updateTaxRate: (item: TaxRate) => void;
    deleteTaxRate: (id: number) => void;

    categoryTaxMap: Record<string, number>;
    updateCategoryTax: (category: string, taxId: number) => void;

    expenseCategories: ExpenseCategory[];
    addExpenseCategory: (item: ExpenseCategory) => void;
    updateExpenseCategory: (item: ExpenseCategory) => void;
    deleteExpenseCategory: (id: number) => void;

    expenses: ExpenseTransaction[];
    addExpense: (item: ExpenseTransaction) => Promise<void>;
    deleteExpense: (id: number) => void;

    customers: Customer[];
    customerAccountMovements: CustomerAccountMovement[]; 
    addCustomerAccountMovement: (item: CustomerAccountMovement) => void;
    addCustomer: (item: Customer) => Promise<void>;
    updateCustomer: (item: Customer) => Promise<void>;
    deleteCustomer: (id: string) => Promise<void>;

    modifierGroups: ModifierGroup[];
    addModifierGroup: (item: ModifierGroup) => void;
    updateModifierGroup: (item: ModifierGroup) => void;
    deleteModifierGroup: (id: number) => void;

    draftOrder: CartItem[] | null;
    setDraftOrder: (items: CartItem[] | null) => void;
    draftOrderType: 'unpaid' | 'paid';
    setDraftOrderType: (type: 'unpaid' | 'paid') => void;

    quickOrderCart: CartItem[];
    updateQuickOrderCart: (items: CartItem[]) => void;

    transactions: Transaction[];
    addTransaction: (transaction: Transaction) => Promise<void>;
    updateTransaction: (id: string, updates: Partial<Transaction>) => void;
    voidTransaction: (id: string, type: 'cancelled' | 'wastage') => void;

    zReports: ZReport[];
    shiftReports: ShiftReport[];
    generateShiftReport: (staff: Staff, payload?: { physicalCash?: number; handoverNote?: string; handoverTo?: string; expenses?: Array<{ description: string; amount: number; paymentSource: 'cash' | 'card' | 'personal' }> }) => Promise<void>;
    closeDay: (targetBusinessDay?: string) => Promise<void>;
    appSettings: AppSettings;
    updateAppSettings: (settings: Partial<AppSettings>) => void;

    loyaltySettings: LoyaltySettings;
    updateLoyaltySettings: (settings: LoyaltySettings) => void;
    addStamp: (customerId: string, count?: number, categoryId?: string) => Promise<void>;
    redeemReward: (customerId: string, selectedProductId?: string) => Promise<void>;
    addLoyaltyCustomer: (name: string, phone?: string) => Promise<void>;
    pendingReward: { productId: number; productName: string } | null;
    setPendingReward: React.Dispatch<React.SetStateAction<{ productId: number; productName: string } | null>>;

    qrMenuSettings: QrMenuSettings;
    updateQrMenuSettings: (settings: QrMenuSettings) => void;


    staff: Staff[];
    addStaff: (member: Staff) => void;
    updateStaff: (member: Staff) => Promise<void>;
    deleteStaff: (id: number) => void;
    updateRole: (role: any) => void;
    hasPermission: (permissionKey: string) => boolean;
    hasFeature: (feature: SubscriptionFeature) => boolean;

    currentUser: Staff | null;
    setCurrentUser: (user: Staff | null) => void;
    // Firebase Auth
    cloudUser: FirebaseUser | null;
    isAuthReady: boolean;
    cafeId: string | null;
    isSyncLocked: boolean;
    syncLockReason: string | null;
    setSyncLock: (locked: boolean, reason?: string | null) => void;

    // Testing State
    isOfflineSimulated: boolean;
    setOfflineSimulated: (val: boolean) => void;

    exportData: () => Promise<void>;
    importData: (file: File) => Promise<void>;
    resetDatabase: (lang?: string) => Promise<void>;
    refreshData: (options?: { fromCloud?: boolean }) => Promise<void>;
    mergeLoyaltyCustomer: (crmCustomerId: string, loyaltyCustomerId: string) => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

const SUBSCRIPTION_SALT = "ki_sub_v1_2024";

function signSubscription(sub: any): string {
  const raw = `${sub.plan}|${sub.status}|${sub.nextBillingDate}|${SUBSCRIPTION_SALT}`;
  let hash = 0;
  for (let i = 0; i < raw.length; i++) {
    const char = raw.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return String(Math.abs(hash));
}

function verifySubscription(sub: any): boolean {
  if (!sub || !sub._sig) return false;
  const expected = signSubscription(sub);
  return sub._sig === expected;
}

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isLoading, setIsLoading] = useState(true);

    // State Definitions



    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [zReports, setZReports] = useState<ZReport[]>([]);
    const [shiftReports, setShiftReports] = useState<ShiftReport[]>([]);
    const [appSettings, setAppSettings] = useState<AppSettings>({
        appName: 'KahveInn',
        appAddress: 'Butik Kahve Evi',
        isSetupComplete: false,
        autoZReport: false,
        autoShiftReport: false,
        workingHoursStart: 8,
        workingHoursEnd: 23,
        taxStrategy: 'inclusive',
        currencySymbol: '₺',
        uiScale: 100,
        subscription: {
            plan: 'trial',
            status: 'active',
            nextBillingDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString() // Default 15 days trial
        },
        reportSettings: {
            showStockCost: false,
            showWastageLoss: false,
            operationalMode: false,
            showNetRevenue: false
        },
        selfServiceMode: false, // Default to standard Table View
        floorPlanMode: false // Varsayılan olarak standart liste görünümü (Grid) aktif
    });

    const [qrMenuSettings, setQrMenuSettings] = useState<QrMenuSettings>({ isActive: true, allowOrdering: true, themeColor: '#d48311', headerText: 'Menüyü İncele & Sipariş Ver', soundEnabled: true });
    const [currentUser, setCurrentUser] = useState<Staff | null>(null);
    const [cloudUser, setCloudUser] = useState<FirebaseUser | null>(null);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [cafeId, setCafeId] = useState<string | null>(localStorage.getItem('cafeId') || null);
    const [isSyncLocked, setIsSyncLocked] = useState(false);
    const [syncLockReason, setSyncLockReason] = useState<string | null>(null);
    const [lastNotification, setLastNotification] = useState<{ tableId: number; timestamp: number } | null>(null);
    const [loadingStatus, setLoadingStatus] = useState('loading.status.starting');

    const [dirtyStockIds, setDirtyStockIds] = useState<Set<number>>(
        () => new Set(parseLocalStorage<number[]>('dirtyStockIds', []))
    );

    // QR Menu Optimization: Track "Dirty" (modified) items for delta sync
    const [dirtyQrProductIds, setDirtyQrProductIds] = useState<Set<number>>(
        () => new Set(parseLocalStorage<number[]>('dirtyQrProductIds', []))
    );

    const [dirtyQrCategoryIds, setDirtyQrCategoryIds] = useState<Set<string>>(
        () => new Set(parseLocalStorage<string[]>('dirtyQrCategoryIds', []))
    );

    const [dirtyQrModifierGroupIds, setDirtyQrModifierGroupIds] = useState<Set<number>>(
        () => new Set(parseLocalStorage<number[]>('dirtyQrModifierGroupIds', []))
    );

    // --- Deletion Optimization (Graveyard) ---
    const [deletedQrProductIds, setDeletedQrProductIds] = useState<Set<number>>(
        () => new Set(parseLocalStorage<number[]>('deletedQrProductIds', []))
    );

    const [deletedQrCategoryIds, setDeletedQrCategoryIds] = useState<Set<string>>(
        () => new Set(parseLocalStorage<string[]>('deletedQrCategoryIds', []))
    );

    const [deletedQrModifierGroupIds, setDeletedQrModifierGroupIds] = useState<Set<number>>(
        () => new Set(parseLocalStorage<number[]>('deletedQrModifierGroupIds', []))
    );

    const markQrProductDirty = (id: number) => {
        setDirtyQrProductIds(prev => {
            const next = new Set(prev).add(id);
            localStorage.setItem('dirtyQrProductIds', JSON.stringify(Array.from(next)));
            return next;
        });
    };

    const markQrCategoryDirty = (id: string) => {
        setDirtyQrCategoryIds(prev => {
            const next = new Set(prev).add(id);
            localStorage.setItem('dirtyQrCategoryIds', JSON.stringify(Array.from(next)));
            return next;
        });
    };

    const markQrModifierGroupDirty = (id: number) => {
        setDirtyQrModifierGroupIds(prev => {
            const next = new Set(prev).add(id);
            localStorage.setItem('dirtyQrModifierGroupIds', JSON.stringify(Array.from(next)));
            return next;
        });
    };

    const markQrProductDeleted = (id: number) => {
        setDeletedQrProductIds(prev => {
            const next = new Set(prev).add(id);
            localStorage.setItem('deletedQrProductIds', JSON.stringify(Array.from(next)));
            return next;
        });
    };

    const markQrCategoryDeleted = (id: string) => {
        setDeletedQrCategoryIds(prev => {
            const next = new Set(prev).add(id);
            localStorage.setItem('deletedQrCategoryIds', JSON.stringify(Array.from(next)));
            return next;
        });
    };

    const markQrModifierGroupDeleted = (id: number) => {
        setDeletedQrModifierGroupIds(prev => {
            const next = new Set(prev).add(id);
            localStorage.setItem('deletedQrModifierGroupIds', JSON.stringify(Array.from(next)));
            return next;
        });
    };

    const clearDirtyQrLists = () => {
        setDirtyQrProductIds(new Set());
        setDirtyQrCategoryIds(new Set());
        setDirtyQrModifierGroupIds(new Set());
        setDeletedQrProductIds(new Set());
        setDeletedQrCategoryIds(new Set());
        setDeletedQrModifierGroupIds(new Set());

        localStorage.removeItem('dirtyQrProductIds');
        localStorage.removeItem('dirtyQrCategoryIds');
        localStorage.removeItem('dirtyQrModifierGroupIds');
        localStorage.removeItem('deletedQrProductIds');
        localStorage.removeItem('deletedQrCategoryIds');
        localStorage.removeItem('deletedQrModifierGroupIds');
    };

    // Testing State
    const [isOfflineSimulated, setOfflineSimulated] = useState(false);

    const [draftOrder, setDraftOrder] = useState<CartItem[] | null>(null);
    const [draftOrderType, setDraftOrderType] = useState<'unpaid' | 'paid'>('unpaid');
    const [quickOrderCart, setQuickOrderCart] = useState<CartItem[]>([]);

    const hasPermission = useCallback((permissionKey: string) => {
        if (!currentUser) return false;
        return (currentUser.permissions ?? []).includes(permissionKey);
    }, [currentUser]);

    const hasFeature = useCallback((feature: SubscriptionFeature): boolean => {
        const sub = appSettings.subscription;
        if (!sub) return false;
    
        if (sub.status !== 'active' && sub.status !== 'past_due') {
            return false;
        }
    
        const now = new Date();
        const nextBilling = new Date(sub.nextBillingDate);
        const isExpired = nextBilling.getTime() < now.getTime();
    
        if (isExpired) {
            return false;
        }
    
        const plan = sub.plan;
    
        // Deneme ve Yönetim paketi: tüm premium özellikler açık
        if (plan === 'trial' || plan === 'management') {
            return true;
        }

        // Temel paket: POS çekirdeği açık, gelişmiş yönetim özellikleri kapalı
        if (plan === 'basic') {
            switch (feature) {
                case 'detailed_reports': return false;
                case 'inventory_tracking': return false;
                case 'cost_tracking': return false;
                case 'recipes': return false;
                case 'online_services': return false;
                case 'staff_management': return false;
                case 'offline_mode': return false;
                case 'loyalty_program': return false;
                case 'product_modifiers': return false;
                case 'ai_assistant': return false;
                default: return true;
            }
        }
    
        return false;
    }, [appSettings.subscription]);

    // --- CORE DATA OPERATIONS ---

    const seedDatabase = async (lang: string = 'tr') => {
        const data = getInitialData(lang);

        await db.transaction('rw', [db.products, db.categories, db.stockItems, db.restaurantTables, db.taxRates, db.modifierGroups, db.sections, db.settings, db.staff, db.expenseCategories, db.customers], async () => {
            await db.products.bulkPut(data.products);
            await db.categories.bulkPut(data.categories);
            await db.stockItems.bulkPut(data.stockItems);
            await db.restaurantTables.bulkPut(data.tables);
            await db.taxRates.bulkPut(data.taxRates);
            await db.modifierGroups.bulkPut(data.modifierGroups);
            await db.sections.bulkPut(data.sections);
            await db.customers.bulkPut(data.customers);

            await db.settings.bulkPut([
                { key: 'appSettings', value: data.settings },
                { key: 'categoryTaxMap', value: {} },
                { key: 'loyaltySettings', value: { mode: 'stamp', stampsForReward: 10, pointsForReward: 100, pointsPerCurrency: 10, rewardName: isTr(lang) ? 'Ücretsiz Kahve' : 'Free Coffee', rewardProductIds: [], autoStamp: true, stampableCategories: [] } },
                { key: 'qrMenuSettings', value: { isActive: true, allowOrdering: true, themeColor: '#d48311', headerText: isTr(lang) ? 'Menüyü İncele & Sipariş Ver' : 'Review Menu & Order', soundEnabled: false } }
            ]);

            await db.staff.bulkPut(data.staff);
            await db.expenseCategories.bulkPut(data.expenseCategories);
        });
    };

    const isTr = (l: string) => l.startsWith('tr');

    // Helper: Fetch subscription from Firestore (called on init + manual refresh only)
    const fetchSubscriptionFromCloud = useCallback(async (cId: string) => {
        try {
            const cafeRef = doc(firestore, 'cafes', cId);
            const docSnap = await getDoc(cafeRef);
            if (docSnap.exists() && docSnap.data().subscription) {
                const data = docSnap.data();
                let nextBillingDate = data.subscription.nextBillingDate;
                if (nextBillingDate && typeof nextBillingDate?.toDate === 'function') {
                    nextBillingDate = nextBillingDate.toDate().toISOString();
                } else if (nextBillingDate instanceof Date) {
                    nextBillingDate = nextBillingDate.toISOString();
                }
                const cleanSubscription = { ...data.subscription, nextBillingDate };
                setAppSettings(prev => ({ ...prev, subscription: cleanSubscription }));
                const signedSubscription = { ...cleanSubscription, _sig: signSubscription(cleanSubscription) };
                const currentSettings = await db.settings.where('key').equals('appSettings').first();
                if (currentSettings?.value) {
                    await db.settings.put({
                        ...currentSettings,
                        value: { ...currentSettings.value, subscription: signedSubscription }
                    });
                } else {
                    // appSettings satırı henüz yoksa oluştur
                    await db.settings.put({ key: 'appSettings', value: { subscription: signedSubscription } });
                }
            }
        } catch (e) {
            console.warn("DataContext: Subscription fetch failed:", e);
        }
    }, []);
    const setSyncLock = useCallback((locked: boolean, reason: string | null = null) => {
        setIsSyncLocked(locked);
        setSyncLockReason(locked ? reason : null);
    }, []);
    const assertMutationAllowed = useCallback((actionName: string) => {
        if (isSyncLocked) {
            throw new Error(`SYNC_LOCKED:${actionName}`);
        }
    }, [isSyncLocked]);
    const refreshData = useCallback(async (options?: { fromCloud?: boolean }) => {
        const loadFromLocalDb = async () => {
            try {
                const [
                    sItems, prods, cats, tabs, sectionsList, taxes, trxs, zReps, sReps, settings, staffList, expCats, expList, modGroups, custs, customerMovements
                ] = await Promise.all([
                    db.stockItems.toArray(),
                    db.products.toArray(),
                    db.categories.toArray(),
                    db.restaurantTables.toArray(),
                    db.sections.toArray(),
                    db.taxRates.toArray(),
                    db.transactions.toArray(),
                    db.zReports.toArray(),
                    db.shiftReports.toArray(),
                    db.settings.toArray(),
                    db.staff.toArray(),
                    db.expenseCategories.toArray(),
                    db.expenses.toArray(),
                    db.modifierGroups.toArray(),
                    db.customers.toArray(),
                    db.customerAccountMovements.toArray(),
                ]);

                setStockItems(sItems);
                setProducts(prods);
                setCategories(cats.sort((a, b) => a.sortOrder - b.sortOrder));
                setTables(tabs);
                setSections(sectionsList);
                setTaxRates(taxes);
                // Eski 'role' alanından permissions'a migration
                const legacyRolePerms: Record<string, string[]> = {
                    'Yönetici': PERMISSIONS.map(p => p.key),
                    'Manager': PERMISSIONS.map(p => p.key),
                    'Admin': PERMISSIONS.map(p => p.key),
                    'Barista': ['VIEW_TABLES', 'MANAGE_STOCK'],
                    'Garson': ['VIEW_TABLES'],
                    'Waiter': ['VIEW_TABLES'],
                    'Mutfak': ['MANAGE_STOCK'],
                    'Kitchen': ['MANAGE_STOCK'],
                };
                const migratedStaff = (staffList as any[]).map((s: any) => {
                    if (Array.isArray(s.permissions)) return s as Staff;
                    return { ...s, permissions: legacyRolePerms[s.role as string] ?? [] } as Staff;
                });
                setStaff(migratedStaff);
                setExpenseCategories(expCats);
                setExpenses(expList.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
                setModifierGroups(modGroups);
                setCustomers(custs);
                setCustomerAccountMovements(customerMovements);

                setTransactions(trxs.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
                setZReports(zReps.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
                setShiftReports(sReps.sort((a, b) => new Date(b.endTime).getTime() - new Date(a.endTime).getTime()));

                const settingsMap = settings.reduce((acc, curr) => ({ ...acc, [curr.key]: curr.value }), {} as any);
                if (settingsMap['appSettings']) {
                    const loadedAppSettings = settingsMap['appSettings'];
                    const loadedSubscription = loadedAppSettings.subscription;
                    if (loadedSubscription) {
                        if (verifySubscription(loadedSubscription)) {
                            const { _sig, ...cleanSubscription } = loadedSubscription;
                            setAppSettings(prev => ({ ...prev, ...loadedAppSettings, subscription: cleanSubscription }));
                        } else {
                            console.warn("Subscription verisi doğrulanamadı, buluttan yenileniyor...");
                            // Doğrulanamayan subscription'ı state'e yansıtmıyoruz (spread edilirse
                            // loadedAppSettings.subscription güvenilmeyen veriyi taşımaya devam eder);
                            // mevcut subscription korunur, cloud fetch başarılı olunca kendi imzasıyla üzerine yazar.
                            const { subscription: _unverifiedSubscription, ...restAppSettings } = loadedAppSettings;
                            setAppSettings(prev => ({ ...prev, ...restAppSettings }));
                            if (cafeId) {
                                await fetchSubscriptionFromCloud(cafeId);
                            }
                        }
                    } else {
                        setAppSettings(prev => ({ ...prev, ...loadedAppSettings }));
                    }
                }
                if (settingsMap['categoryTaxMap']) setCategoryTaxMap(settingsMap['categoryTaxMap']);
                if (settingsMap['loyaltySettings']) setLoyaltySettings(settingsMap['loyaltySettings']);

                if (settingsMap['qrMenuSettings']) {
                    setQrMenuSettings(settingsMap['qrMenuSettings']);
                } else if (settingsMap['qrsettings']) {
                    setQrMenuSettings(settingsMap['qrsettings']);
                }

                if (staffList.length > 0 && currentUser) {
                    const currentId = currentUser.id;
                    const exists = staffList.find(s => s.id === currentId);
                    if (exists) setCurrentUser(exists);
                    else setCurrentUser(null);
                }
            } catch (error) {
                console.error("Failed to load data from Dexie:", error);
            }
        };

        await loadFromLocalDb();

        if (options?.fromCloud && cafeId) {
            await fetchSubscriptionFromCloud(cafeId);
        }
    }, [currentUser?.id, cafeId, fetchSubscriptionFromCloud]);

    // Initial Data Load
    useEffect(() => {
        const initDB = async () => {
            try {
                setLoadingStatus('loading.status.init_db');
                const count = await db.products.count();
                if (count === 0) {
                    setLoadingStatus('loading.status.seeding');
                    await seedDatabase();
                } else {
                    const catCount = await db.categories.count();
                    if (catCount === 0) {
                        await db.categories.bulkPut(INITIAL_CATEGORIES);
                    }

                    const existingStocks = await db.stockItems.toArray();
                    const stockIds = new Set(existingStocks.map(s => s.id));
                    const missingStocks = INITIAL_STOCK.filter(s => !stockIds.has(s.id));
                    if (missingStocks.length > 0) await db.stockItems.bulkPut(missingStocks);

                }

                setLoadingStatus('loading.status.refreshing');
                await refreshData();
            } catch (error) {
                console.error("Initialization error:", error);
            } finally {
                setLoadingStatus('loading.status.ready');
                setTimeout(() => setIsLoading(false), 500);
            }
        };

        initDB();

        // Firebase Auth Listener (Optimized: localStorage first, Firestore only when needed)
        const unsubscribe = onIdTokenChanged(auth, async (user) => {
            if (user) {
                const lastUid = localStorage.getItem('kahveinn_last_uid');
                if (lastUid && lastUid !== user.uid) {
                    try {
                        await resetDatabase();
                    } catch (e) {
                        console.error("DataContext: Failed to reset local data on account switch:", e);
                    }
                }
                localStorage.setItem('kahveinn_last_uid', user.uid);
            }

            setCloudUser(user);

            if (user) {
                try {
                    let cId: string | null = null;

                    const tokenResult = await user.getIdTokenResult(false);
                    cId = tokenResult.claims.cafeId as string | null;

                    if (!cId) {
                        const cached = localStorage.getItem('cafeId');
                        const cachedUid = localStorage.getItem('cafeId_uid');
                        if (cached && cachedUid === user.uid) {
                            cId = cached;
                        }
                    }

                    if (!cId) {
                        try {
                            const userRef = doc(firestore, 'users', user.uid);
                            const userSnap = await getDoc(userRef);
                            const mappedCafeId = userSnap.exists() ? userSnap.data().cafeId : null;

                            if (typeof mappedCafeId === 'string' && mappedCafeId) {
                                cId = mappedCafeId;
                            } else {
                                console.warn("DataContext: No CafeID claim AND no user mapping found.");
                            }
                        } catch (mappingErr) {
                            console.error("DataContext: User mapping lookup failed:", mappingErr);
                        }
                    }

                    if (cId) {
                        setCafeId(cId);
                        localStorage.setItem('cafeId', cId);
                        localStorage.setItem('cafeId_uid', user.uid);
                    } else {
                        setCafeId(null);
                        localStorage.removeItem('cafeId');
                        localStorage.removeItem('cafeId_uid');
                    }
                } catch (e) {
                    console.error("Error fetching claims/cafe:", e);
                    setCafeId(null);
                    showToast('Bağlantı hatası: oturum yenilenemedi. Sayfayı yenileyin.', 'error');
                }
            } else {
                setCafeId(null);
                localStorage.removeItem('cafeId');
                localStorage.removeItem('cafeId_uid');
            }

            setIsAuthReady(true);
        });

        return () => unsubscribe();
    }, []);

    // --- Subscription Sync: One-time fetch on init (no real-time listener - saves Firebase reads)
    useEffect(() => {
        if (!cafeId) return;
        let mounted = true;
        const run = async () => {
            try {
                const cafeRef = doc(firestore, 'cafes', cafeId);
                const docSnap = await getDoc(cafeRef);
                if (!mounted) return;
                if (docSnap.exists() && docSnap.data().subscription) {
                    const data = docSnap.data();
                    let nextBillingDate = data.subscription.nextBillingDate;
                    if (nextBillingDate && typeof nextBillingDate?.toDate === 'function') {
                        nextBillingDate = nextBillingDate.toDate().toISOString();
                    } else if (nextBillingDate instanceof Date) {
                        nextBillingDate = nextBillingDate.toISOString();
                    }
                    const cleanSubscription = { ...data.subscription, nextBillingDate };
                    setAppSettings(prev => ({ ...prev, subscription: cleanSubscription }));
                    const signedSubscription = { ...cleanSubscription, _sig: signSubscription(cleanSubscription) };
                    const currentSettings = await db.settings.where('key').equals('appSettings').first();
                    if (currentSettings?.value) {
                        await db.settings.put({
                            ...currentSettings,
                            value: { ...currentSettings.value, subscription: signedSubscription }
                        });
                    }
                }
            } catch (e) {
                if (mounted) console.warn("DataContext: Init subscription fetch failed:", e);
            }
        };
        run();
        return () => { mounted = false; };
    }, [cafeId]);

    // --- Data Management Functions ---
    const exportData = async () => {
        const data = {
            stockItems: await db.stockItems.toArray(),
            products: await db.products.toArray(),
            categories: await db.categories.toArray(),
            customers: await db.customers.toArray(),
            customerAccountMovements: await db.customerAccountMovements.toArray(),
            restaurantTables: await db.restaurantTables.toArray(),
            taxRates: await db.taxRates.toArray(),
            transactions: await db.transactions.toArray(),
            zReports: await db.zReports.toArray(),
            shiftReports: await db.shiftReports.toArray(),
            settings: await db.settings.toArray(),
            staff: await db.staff.toArray(),
            expenseCategories: await db.expenseCategories.toArray(),
            expenses: await db.expenses.toArray(),
            modifierGroups: await db.modifierGroups.toArray(),
            sections: await db.sections.toArray(),
            roles: await db.roles.toArray(),
            timestamp: new Date().toISOString(),
            version: 1
        };

        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `KahveInn_Backup_${new Date().toISOString().slice(0, 10)}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const importData = async (file: File) => {
        return new Promise<void>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const data = JSON.parse(e.target?.result as string);
                    if (!data.version) throw new Error("Geçersiz yedek dosyası");

                    await (db as any).transaction('rw', db.stockItems, db.products, db.categories, db.customers, db.customerAccountMovements, db.restaurantTables, db.taxRates, db.transactions, db.zReports, db.shiftReports, db.settings, db.staff, db.expenseCategories, db.expenses, db.modifierGroups, db.sections, db.roles, async () => {
                        await Promise.all([
                            db.stockItems.clear(), db.products.clear(), db.categories.clear(), db.customers.clear(), db.customerAccountMovements.clear(), db.restaurantTables.clear(),
                            db.taxRates.clear(), db.transactions.clear(), db.zReports.clear(),
                            db.shiftReports.clear(), db.settings.clear(), db.staff.clear(),
                            db.expenseCategories.clear(), db.expenses.clear(), db.modifierGroups.clear(),
                            db.sections.clear(), db.roles.clear()
                        ]);

                        if (data.stockItems) await db.stockItems.bulkAdd(data.stockItems);
                        if (data.products) await db.products.bulkAdd(data.products);

                        if (data.categories && Array.isArray(data.categories) && data.categories.length > 0) {
                            await db.categories.bulkAdd(data.categories);
                        } else {
                            await db.categories.bulkAdd(INITIAL_CATEGORIES);
                        }
                        if (data.customers) await db.customers.bulkAdd(data.customers);
                        if (data.customerAccountMovements) await db.customerAccountMovements.bulkAdd(data.customerAccountMovements);
                        if (data.restaurantTables) await db.restaurantTables.bulkAdd(data.restaurantTables);
                        if (data.taxRates) await db.taxRates.bulkAdd(data.taxRates);
                        if (data.transactions) await db.transactions.bulkAdd(data.transactions);
                        if (data.zReports) await db.zReports.bulkAdd(data.zReports);
                        if (data.shiftReports) await db.shiftReports.bulkAdd(data.shiftReports);
                        if (data.settings) await db.settings.bulkAdd(data.settings);
                        if (data.staff) await db.staff.bulkAdd(data.staff);
                        if (data.expenseCategories) await db.expenseCategories.bulkAdd(data.expenseCategories);
                        if (data.expenses) await db.expenses.bulkAdd(data.expenses);
                        if (data.modifierGroups) await db.modifierGroups.bulkAdd(data.modifierGroups);
                        if (data.sections) await db.sections.bulkAdd(data.sections);
                        if (data.roles) await db.roles.bulkAdd(data.roles);
                    });
                    await refreshData();
                    resolve();
                } catch (err) {
                    console.error(err);
                    reject(err);
                }
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsText(file);
        });
    };

    const resetDatabase = async (lang: string = 'tr') => {
        await (db as any).transaction('rw', db.stockItems, db.products, db.categories, db.customers, db.customerAccountMovements, db.restaurantTables, db.taxRates, db.transactions, db.zReports, db.shiftReports, db.settings, db.staff, db.expenseCategories, db.expenses, db.modifierGroups, db.sections, db.roles, db.syncStack, async () => {
            await Promise.all([
                db.stockItems.clear(), db.products.clear(), db.categories.clear(), db.customers.clear(), db.customerAccountMovements.clear(), db.restaurantTables.clear(),
                db.taxRates.clear(), db.transactions.clear(), db.zReports.clear(),
                db.shiftReports.clear(), db.settings.clear(), db.staff.clear(),
                db.expenseCategories.clear(), db.expenses.clear(), db.modifierGroups.clear(),
                db.sections.clear(), db.roles.clear(),
                db.syncStack.clear()
            ]);
        });

        await seedDatabase(lang);
        await refreshData();
    };

    const addToSyncQueue = async (item: any) => {
        if (item.action === 'update' && item.data && item.data.id) {
            try {
                const pendingItems = await db.syncStack.where('type').equals(item.type).toArray();
                const existing = pendingItems.find(i =>
                    i.action === 'update' &&
                    i.status === 'pending' &&
                    i.data && i.data.id === item.data.id
                );

                if (existing && existing.id) {
                    const result = await db.syncStack.update(existing.id, {
                        data: item.data,
                        timestamp: Date.now()
                    });
                    window.dispatchEvent(new CustomEvent('kahveinn:syncqueued'));
                    return result;
                }
            } catch (err) {
                console.warn("[Sync Optimization] Coalescing check failed:", err);
            }
        }

        const result = await db.syncStack.add({ status: 'pending', timestamp: Date.now(), ...item });
        window.dispatchEvent(new CustomEvent('kahveinn:syncqueued'));
        return result;
    };

    const { modifierGroups, setModifierGroups, addModifierGroup, updateModifierGroup, deleteModifierGroup } = useModifierGroups(addToSyncQueue);
    const { taxRates, setTaxRates, categoryTaxMap, setCategoryTaxMap, addTaxRate, updateTaxRate, deleteTaxRate, updateCategoryTax } = useTaxRates(addToSyncQueue);
    const { sections, setSections, addSection, updateSection, deleteSection } = useSections(addToSyncQueue);
    const { expenseCategories, setExpenseCategories, expenses, setExpenses, addExpenseCategory, updateExpenseCategory, deleteExpenseCategory, addExpense, deleteExpense } = useExpenses(addToSyncQueue, assertMutationAllowed);
    const { staff, setStaff, addStaff, updateStaff, deleteStaff } = useStaff(addToSyncQueue, assertMutationAllowed, currentUser, setCurrentUser);

    // Auto-heal: cafeId ilk resolve edildiğinde hiç staff kaydı yoksa (ör. registerCafe
    // sonrası setup akışının staff seed'lemeden yarıda kaldığı eski/kırık hesaplar),
    // varsayılan admin personelini otomatik ekler. resetDatabase'in normal akıştaki
    // seed'ini tekrarlar; sadece db.staff boşsa çalışır, mevcut kayıtları asla ezmez.
    useEffect(() => {
        if (!cafeId) return;
        (async () => {
            try {
                const existingCount = await db.staff.count();
                if (existingCount > 0) return;

                const lang = localStorage.getItem('i18nextLng') || 'tr';
                const defaultAdmin = getInitialData(lang).staff.find(s => s.isAdmin);
                if (!defaultAdmin) return;

                await addStaff(defaultAdmin);
                console.warn('DataContext: Auto-heal - varsayılan admin personeli eklendi (staff koleksiyonu boştu).');
            } catch (e) {
                console.error('DataContext: Staff auto-heal failed:', e);
            }
        })();
    }, [cafeId]);
    const { customers, setCustomers, customerAccountMovements, setCustomerAccountMovements, addCustomerAccountMovement, addCustomer, updateCustomer, deleteCustomer, mergeLoyaltyCustomer } = useCustomers(addToSyncQueue, assertMutationAllowed);
    const { products, setProducts, categories, setCategories, addProduct, updateProduct, deleteProduct, addCategory, updateCategory, deleteCategory } = useProducts(addToSyncQueue, assertMutationAllowed, markQrProductDirty, markQrProductDeleted, markQrCategoryDirty, markQrCategoryDeleted);
    const { loyaltySettings, setLoyaltySettings, pendingReward, setPendingReward, updateLoyaltySettings, addStamp, redeemReward, addLoyaltyCustomer } = useLoyalty(addToSyncQueue, refreshData, customers, products);
    const { stockItems, setStockItems, addStockItem, updateStockItem, deleteStockItem, reportWastage } = useStock(addToSyncQueue, assertMutationAllowed, setDirtyStockIds, expenseCategories, setExpenseCategories, addExpense);
    const { tables, setTables, addTable, updateTable, resetTable, transferTable, transferTableItems, sendQrOrder, deleteTable } = useTables(addToSyncQueue, setLastNotification, hasPermission);
    const { showToast } = useToast();

    const addTransaction = async (transaction: Transaction) => {
        assertMutationAllowed('addTransaction');
        const stockDeductions: Record<number, number> = {};
        const wastageCalculations: { stockId: number, amount: number, name: string }[] = [];
        const stockReasons: Record<number, 'recipe' | 'modifier'> = {};
        const snapshotErrors: string[] = [];
        const addToDeduction = (id: number, amount: number, reason: 'recipe' | 'modifier') => {
            stockDeductions[id] = (stockDeductions[id] || 0) + amount;
            if (!stockReasons[id]) stockReasons[id] = reason;
        };
        const resolveModifierGroup = (modifierId: string, groupId?: number) => {
            let group = modifierGroups.find(g => g.id === groupId);
        
            if (!group) {
                group = modifierGroups.find(g => g.options.some(o => o.id === modifierId));
            }
        
            return group;
        };
        transaction.items.forEach(cartItem => {
            if (cartItem.status === 'cancelled') return;

            const liveProduct = products.find(p => p.id === cartItem.id);
const product: Product = liveProduct || (cartItem as Product);

if (!liveProduct) {
    console.warn(`[Snapshot Fallback] Product cache miss for cart item id=${cartItem.id}. Using transaction item snapshot.`);
}

if (!product) {
    snapshotErrors.push(`Product not found for cart item id=${cartItem.id}`);
    return;
}
            const overriddenStockIds = new Set<number>();

            if (cartItem.modifiers) {
                cartItem.modifiers.forEach(mod => {
                    let group = modifierGroups.find(g => g.id === mod.groupId);
            
                    if (!group) {
                        group = modifierGroups.find(g => g.options.some(o => o.id === mod.id));
                    }
            
                    if (!group) {
                        snapshotErrors.push(`Modifier group not found for modifier id=${mod.id}`);
                        return;
                    }
            
                    if (!group.allowMultiple) {
                        group.options.forEach(opt => {
                            if (opt.stockId) overriddenStockIds.add(opt.stockId);
                        });
                    }
                });
            }

            if (product) {
                // Variant recipe takes priority if a variant was selected and has its own recipe
                const variantRecipe = cartItem.selectedVariantId
                    ? product.variants?.find(v => v.id === cartItem.selectedVariantId)?.recipe
                    : undefined;
                const activeRecipe = variantRecipe ?? product.recipe;

                if (activeRecipe) {
                    activeRecipe.forEach(ingredient => {
                        if (overriddenStockIds.has(ingredient.stockId)) return;
                        const amountToDeduct = ingredient.amount * cartItem.quantity;
                        addToDeduction(ingredient.stockId, amountToDeduct, 'recipe');

                        if (cartItem.status === 'wastage') {
                            const stock = stockItems.find(s => s.id === ingredient.stockId);
                            if (stock) {
                                wastageCalculations.push({
                                    stockId: ingredient.stockId,
                                    amount: amountToDeduct,
                                    name: stock.name
                                });
                            }
                        }
                    });
                }
            }

            if (product) {
                const shouldTrack = product.trackStock ?? (product.stock !== undefined && product.stock > 0 && product.stock < 9999);
                if (shouldTrack) {
                    const newStock = Math.max(0, (product.stock || 0) - cartItem.quantity);
                    updateProduct({ ...product, stock: newStock }, true);
                }
            }

            if (cartItem.modifiers && cartItem.modifiers.length > 0) {
                cartItem.modifiers.forEach(mod => {
                    let group = modifierGroups.find(g => g.id === mod.groupId);
            
                    if (!group) {
                        group = modifierGroups.find(g => g.options.some(o => o.id === mod.id));
                    }
            
                    if (!group) {
                        snapshotErrors.push(`Modifier group not found for modifier id=${mod.id}`);
                        return;
                    }
            
                    const option = group.options.find(o => o.id === mod.id);
                    if (!option) {
                        snapshotErrors.push(`Modifier option not found for modifier id=${mod.id}`);
                        return;
                    }
            
                    if (option.stockId) {
                        let deductionUnit = option.amount || 0;
            
                        if (group.isMilkSelect && product?.milkStockAmount && product.milkStockAmount > 0) {
                            deductionUnit = product.milkStockAmount;
                        }
            
                        if (group.isSyrupSelect) {
                            let multiplier = 1;
                            if (product?.syrupOverrides && product.syrupOverrides[option.id] !== undefined) {
                                multiplier = product.syrupOverrides[option.id];
                            } else if (product?.syrupPumpCount && product.syrupPumpCount > 0) {
                                multiplier = product.syrupPumpCount;
                            }
                            deductionUnit = (option.amount || 0) * multiplier;
                        }
            
                        if (deductionUnit > 0) {
                            const amountToDeduct = deductionUnit * cartItem.quantity;
                            addToDeduction(option.stockId, amountToDeduct, 'modifier');
            
                            if (cartItem.status === 'wastage') {
                                const stock = stockItems.find(s => s.id === option.stockId);
                                if (stock) {
                                    wastageCalculations.push({
                                        stockId: option.stockId,
                                        amount: amountToDeduct,
                                        name: stock.name
                                    });
                                }
                            }
                        }
                    }
                });
            }
        });
        if (snapshotErrors.length > 0) {
            throw new Error(`Stock snapshot could not be created: ${snapshotErrors.join(' | ')}`);
        }
        const stockEffects: Transaction['stockEffects'] = Object.entries(stockDeductions).map(([stockIdStr, amount]) => {
            const stockId = Number(stockIdStr);
            const reason = stockReasons[stockId] || 'manual';
            return {
                stockId,
                delta: -amount,
                reason
            };
        });
        const transactionWithSnapshot: Transaction = {
            ...transaction,
            stockModelVersion: 1,
            stockEffectsSource: 'snapshot',
            stockEffects
        };

        await db.transactions.add(transactionWithSnapshot);
        await addToSyncQueue({ type: 'transaction', action: 'create', data: transactionWithSnapshot, status: 'pending', timestamp: Date.now() });
        setTransactions(prev => [transactionWithSnapshot, ...prev]);
        const updatePromises: Promise<any>[] = [];
        Object.entries(stockDeductions).forEach(([stockIdStr, amount]) => {
            const stockId = Number(stockIdStr);
            const stockItem = stockItems.find(s => s.id === stockId);
            if (stockItem) {
                const updatedItem = { ...stockItem, stock: Math.max(0, stockItem.stock - amount) };
                updatePromises.push(updateStockItem(updatedItem, true));
            }
        });

        if (wastageCalculations.length > 0) {
            let catId: number;
            const existingCat = expenseCategories.find(c => c.isWastage || c.name.includes("Zaiyat") || c.name.includes("Fire"));
            if (existingCat) {
                catId = existingCat.id;
            } else {
                const newCat: Omit<ExpenseCategory, 'id'> = { name: "Zaiyat / Fire", description: "Otomatik zaiyat kaydı", isRecurring: false, isWastage: true };
                const id = await db.expenseCategories.add(newCat as ExpenseCategory);
                catId = id as number;
                setExpenseCategories(prev => [...prev, { ...newCat, id: catId }]);
            }

            let totalLoss = 0;
            let details = "";

            wastageCalculations.forEach(w => {
                const stock = stockItems.find(s => s.id === w.stockId);
                if (stock) {
                    totalLoss += w.amount * stock.cost;
                    details += `${w.name}: ${w.amount.toFixed(3)} ${stock.unit}, `;
                }
            });

            if (totalLoss > 0) {
                const expense: ExpenseTransaction = {
                    id: Date.now() + 1,
                    categoryId: catId,
                    categoryName: "Zaiyat / Fire",
                    amount: totalLoss,
                    description: `Sipariş Zaiyatı (${transaction.tableName}): ${details.slice(0, 100)}...`,
                    date: new Date().toISOString()
                };
                await addExpense(expense);
            }
        }

        await Promise.all(updatePromises);
    };

    const updateTransaction = async (id: string, updates: Partial<Transaction>) => {
        await db.transactions.update(id, updates);
        setTransactions(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));

        const updatedTransaction = transactions.find(t => t.id === id);
        if (updatedTransaction) {
            const finalData = { ...updatedTransaction, ...updates };
            await addToSyncQueue({
                type: 'transaction',
                action: 'update',
                data: finalData,
                status: 'pending',
                timestamp: Date.now()
            });
        }
    };

    const voidTransaction = async (id: string, type: 'cancelled' | 'wastage') => {
        const targetId = String(id);
        const transaction = transactions.find(t => String(t.id) === targetId);
        if (!transaction) return;

        const prevStatus = transaction.status || 'completed';
        if (prevStatus === type) return;

        let stockOperation = 0;
        if (type === 'cancelled' && prevStatus !== 'cancelled') stockOperation = 1;
        else if (prevStatus === 'cancelled' && type !== 'cancelled') stockOperation = -1;

        await db.transactions.update(targetId, { status: type });
        setTransactions(prev => prev.map(t => String(t.id) === targetId ? { ...t, status: type } : t));

        await addToSyncQueue({
            type: 'transaction',
            action: 'update',
            data: { ...transaction, status: type },
            status: 'pending',
            timestamp: Date.now()
        });

        if (stockOperation !== 0) {
            const newStockItems = [...stockItems];
            const newProducts = [...products];
            const updatePromises: Promise<any>[] = [];
            const newDirtyIds: number[] = [];

            if (transaction.stockEffects && transaction.stockEffects.length > 0) {
                transaction.stockEffects.forEach(effect => {
                    const stockIndex = newStockItems.findIndex(s => s.id === effect.stockId);
                    if (stockIndex !== -1) {
                        const newItem = {
                            ...newStockItems[stockIndex],
                            stock: newStockItems[stockIndex].stock + (-effect.delta * stockOperation)
                        };
                        newStockItems[stockIndex] = newItem;
                        updatePromises.push(db.stockItems.put(newItem));
                        newDirtyIds.push(newItem.id);
                    }
                });
            }
            else {
                console.warn('Legacy transaction without stockEffects; stock item restore skipped.', transaction.id);
            }
            transaction.items.forEach(cartItem => {
                const qty = cartItem.quantity * stockOperation;
                const productIndex = newProducts.findIndex(p => p.id === cartItem.id);
                if (productIndex !== -1) {
                    const p = newProducts[productIndex];
                    const shouldTrack = p.trackStock ?? (p.stock !== undefined && p.stock > 0 && p.stock < 9999);

                    if (shouldTrack) {
                        const newProduct = { ...p, stock: (p.stock || 0) + qty };
                        newProducts[productIndex] = newProduct;
                        updatePromises.push(db.products.put(newProduct));
                    }
                }
            });

            await Promise.all(updatePromises);
            setStockItems(newStockItems);
            setProducts(newProducts);

            if (newDirtyIds.length > 0) {
                setDirtyStockIds(prev => {
                    const next = new Set(prev);
                    newDirtyIds.forEach(id => next.add(id));
                    localStorage.setItem('dirtyStockIds', JSON.stringify(Array.from(next)));
                    return next;
                });
            }
        }
    };

    const generateShiftReport = async (staffMember: Staff, payload?: { physicalCash?: number; handoverNote?: string; handoverTo?: string; expenses?: Array<{ description: string; amount: number; paymentSource: 'cash' | 'card' | 'personal' }> }) => {
        const now = new Date();
        const startHour = appSettings.workingHoursStart ?? 8;
        const businessDayStart = new Date(now);
        if (now.getHours() < startHour) businessDayStart.setDate(businessDayStart.getDate() - 1);
        businessDayStart.setHours(startHour, 0, 0, 0);

        const staffTrx = transactions.filter(t =>
            t.staffId === staffMember.id &&
            !t.isArchived &&
            new Date(t.date) >= businessDayStart
        );

        const salesTrx = staffTrx.filter(t => t.status !== 'cancelled' && t.status !== 'wastage');

        let cash = 0, card = 0, other = 0;
        const totalSales = salesTrx.reduce((acc, t) => acc + t.total, 0);

        salesTrx.forEach(t => {
            if (t.payments && t.payments.length > 0) {
                t.payments.forEach(p => {
                    if (p.method === PaymentMethod.Cash) cash += p.amount;
                    else if (p.method === PaymentMethod.Card) card += p.amount;
                    else other += p.amount;
                });
            } else {
                if (t.paymentMethod === PaymentMethod.Cash) cash += t.total;
                else if (t.paymentMethod === PaymentMethod.Card) card += t.total;
                else other += t.total;
            }
        });

        const cashDifference = payload?.physicalCash !== undefined
            ? payload.physicalCash - cash
            : undefined;

        const report: ShiftReport = {
            id: `SHIFT-${Date.now()}`,
            cafeId: cafeId || '',
            staffId: staffMember.id,
            staffName: staffMember.name,
            startTime: businessDayStart.toISOString(),
            endTime: now.toISOString(),
            totalSales,
            cashTotal: cash,
            cardTotal: card,
            otherTotal: other,
            transactionCount: staffTrx.length,
            ...(payload?.physicalCash !== undefined && { physicalCash: payload.physicalCash }),
            ...(cashDifference !== undefined && { cashDifference }),
            ...(payload?.handoverNote && { handoverNote: payload.handoverNote }),
            ...(payload?.handoverTo && { handoverTo: payload.handoverTo }),
            ...(payload?.expenses?.length && { expenses: payload.expenses }),
        };

        await db.shiftReports.add(report);
        setShiftReports(prev => [report, ...prev]);

        if (payload?.expenses?.length) {
          let shiftCat = expenseCategories.find(c => c.name === 'Vardiya Gideri');
          if (!shiftCat) {
            const newCatId = await db.expenseCategories.add({
              name: 'Vardiya Gideri',
              distributeMonthly: false
            } as ExpenseCategory);
            shiftCat = { id: newCatId as number, name: 'Vardiya Gideri', distributeMonthly: false };
            setExpenseCategories(prev => [...prev, shiftCat!]);
          }
          for (const e of payload.expenses) {
            await addExpense({
              id: 0,
              categoryId: shiftCat.id,
              categoryName: 'Vardiya Gideri',
              amount: e.amount,
              description: `${e.description} (${e.paymentSource})`,
              date: new Date().toISOString(),
            } as ExpenseTransaction);
          }
        }

        await db.syncStack.add({
            type: 'shiftReport',
            action: 'create',
            data: report,
            status: 'pending',
            timestamp: Date.now()
        });
    };

    const closeDay = async (targetBusinessDay?: string) => {
        const startHour = appSettings.workingHoursStart ?? 8;
        const businessDay = targetBusinessDay || getBusinessDayKey(new Date(), startHour);
        const activeTrx = transactions.filter(t => !t.isArchived && getBusinessDayKey(t.date || '', startHour) === businessDay);
        if (activeTrx.length === 0) return;

        let total = 0, cash = 0, card = 0, other = 0, tax = 0;
        activeTrx.forEach(t => {
            if (!t.status || t.status === 'completed') {
                total += t.total;
                if (t.tax !== undefined) {
                    tax += t.tax;
                } else {
                    (t.items || []).forEach(item => {
                        if (item.status === 'cancelled' || item.status === 'wastage') return;
                        const itemTotal = item.price * item.quantity;
                        const taxId = categoryTaxMap[item.category];
                        const taxRate = taxRates.find(r => r.id === taxId)?.rate || 0;
                        tax += itemTotal - (itemTotal / (1 + taxRate / 100));
                    });
                }
                if (t.payments && t.payments.length > 0) {
                    t.payments.forEach(p => {
                        if (p.method === PaymentMethod.Cash) cash += p.amount;
                        else if (p.method === PaymentMethod.Card) card += p.amount;
                        else other += p.amount;
                    });
                } else {
                    if (t.paymentMethod === PaymentMethod.Cash) cash += t.total;
                    else if (t.paymentMethod === PaymentMethod.Card) card += t.total;
                    else other += t.total;
                }
            }
        });

        const wastageCatIds = new Set(expenseCategories.filter(c => c.isWastage).map(c => c.id));
        const wastageTotal = expenses
            .filter(e =>
                getBusinessDayKey(e.date || '', startHour) === businessDay &&
                (wastageCatIds.has(e.categoryId) ||
                    e.categoryName?.toLowerCase().includes('zaiyat') ||
                    e.categoryName?.toLowerCase().includes('fire'))
            )
            .reduce((acc, e) => acc + e.amount, 0);

        const zReport: ZReport = {
            id: `Z-${Date.now()}`,
            date: targetBusinessDay ? `${businessDay}T12:00:00.000Z` : new Date().toISOString(),
            total,
            cash,
            card,
            other,
            transactionsCount: activeTrx.filter(t => !t.status || t.status === 'completed').length,
            timestamp: new Date().toISOString(),
            staff: currentUser?.name || 'Sistem',
            wastageTotal: wastageTotal > 0 ? wastageTotal : undefined,
            tax: tax > 0 ? tax : undefined,
        };

        await db.zReports.add(zReport);
        setZReports(prev => [zReport, ...prev]);

        await db.syncStack.add({
            type: 'zReport',
            action: 'create',
            data: zReport,
            status: 'pending',
            timestamp: Date.now()
        });

        const updatedTrx = activeTrx.map(t => ({ ...t, isArchived: true }));
        await Promise.all(updatedTrx.map(t => db.transactions.put(t)));
        await Promise.all(updatedTrx.map(t => db.syncStack.add({
            type: 'transaction',
            action: 'update',
            data: t,
            status: 'pending',
            timestamp: Date.now()
        })));

        setTransactions(prev => prev.map(t => {
            const archived = updatedTrx.find(ut => ut.id === t.id);
            return archived || t;
        }));

        const busyTables = tables.filter(t => t.status !== 'empty');
        await Promise.all(busyTables.map(async (t) => {
            const clean: Table = { ...t, status: 'empty', orders: [], total: 0, incomingOrder: undefined };
            await db.restaurantTables.put(clean);
            
            // EKSİK OLAN KİLİT NOKTA: Sıfırlanan masayı Firebase'e bildir
            await addToSyncQueue({
                type: 'table',
                action: 'update',
                data: clean,
                status: 'pending',
                timestamp: Date.now()
            });
            
            return clean;
        }));
        setTables(prev => prev.map(t => ({ ...t, status: 'empty', orders: [], total: 0, incomingOrder: undefined })));

        const dirtyIdsArray = Array.from(dirtyStockIds);
        if (dirtyIdsArray.length > 0) {
            const stockSyncPromises = dirtyIdsArray.map(id => {
                const item = stockItems.find(s => s.id === id);
                if (item) {
                    return db.syncStack.add({
                        type: 'stockItem',
                        action: 'update',
                        data: item,
                        status: 'pending',
                        timestamp: Date.now()
                    });
                }
                return Promise.resolve();
            });
            await Promise.all(stockSyncPromises);
            setDirtyStockIds(new Set());
            localStorage.removeItem('dirtyStockIds');
        }
    };

    const updateAppSettings = async (settings: Partial<AppSettings>) => {
        const newSettings = { ...appSettings, ...settings };
        setAppSettings(newSettings);
        // subscription her zaman bellekte imzasız tutulur (bkz. refreshData'daki _sig stripping);
        // burada yeniden imzalanmazsa ilgisiz bir ayar değişikliği bile kayıtlı _sig'i siler
        // ve bir sonraki yüklemede verifySubscription() aboneliği null'a düşürür.
        const persistedSettings = newSettings.subscription
            ? { ...newSettings, subscription: { ...newSettings.subscription, _sig: signSubscription(newSettings.subscription) } }
            : newSettings;
        await db.settings.put({ key: 'appSettings', value: persistedSettings });
        await addToSyncQueue({
            type: 'setting',
            action: 'update',
            data: { key: 'appSettings', value: persistedSettings },
            status: 'pending',
            timestamp: Date.now()
        });
    };

    const updateQrMenuSettings = async (settings: QrMenuSettings) => {
        if (JSON.stringify(settings) === JSON.stringify(qrMenuSettings)) {
            return;
        }
        setQrMenuSettings(settings);
        await db.settings.put({ key: 'qrMenuSettings', value: settings });
        await addToSyncQueue({
            type: 'setting',
            action: 'update',
            data: { key: 'qrMenuSettings', value: settings },
            status: 'pending',
            timestamp: Date.now()
        });
    };

    const updateRole = async (_role: any) => {};
    if (isLoading) {
        return <LoadingScreen status={loadingStatus} />;
    }

    return (
        <DataContext.Provider value={{
            stockItems, addStockItem, updateStockItem, deleteStockItem, reportWastage,
            products, addProduct, updateProduct, deleteProduct,
            categories, addCategory, updateCategory, deleteCategory,
            tables, addTable, updateTable, deleteTable, resetTable, transferTable,
            transferTableItems,
            sections, addSection, updateSection, deleteSection,
            taxRates, addTaxRate, updateTaxRate, deleteTaxRate,
            categoryTaxMap, updateCategoryTax,
            expenseCategories, addExpenseCategory, updateExpenseCategory, deleteExpenseCategory,
            expenses, addExpense, deleteExpense,
            modifierGroups, addModifierGroup, updateModifierGroup, deleteModifierGroup,
            draftOrder, setDraftOrder, draftOrderType, setDraftOrderType,
            quickOrderCart, updateQuickOrderCart: setQuickOrderCart,
            transactions, addTransaction, updateTransaction, voidTransaction,
            zReports, shiftReports, generateShiftReport, closeDay, appSettings, updateAppSettings,
            loyaltySettings, updateLoyaltySettings, addStamp, redeemReward, addLoyaltyCustomer, pendingReward, setPendingReward, qrMenuSettings, updateQrMenuSettings,
            sendQrOrder,
            lastNotification,
            customers, customerAccountMovements, addCustomerAccountMovement, addCustomer, updateCustomer, deleteCustomer,
            staff, addStaff, updateStaff, deleteStaff,
            updateRole, hasPermission,
            hasFeature,
            currentUser, setCurrentUser,
            cloudUser, isAuthReady, cafeId,
            isSyncLocked, syncLockReason, setSyncLock,
            isOfflineSimulated, setOfflineSimulated,
            dirtyQrProductIds, dirtyQrCategoryIds, dirtyQrModifierGroupIds, clearDirtyQrLists,
            deletedQrProductIds, deletedQrCategoryIds, deletedQrModifierGroupIds,
            markQrProductDeleted, markQrCategoryDeleted, markQrModifierGroupDeleted,
            exportData, importData, resetDatabase, refreshData,
            mergeLoyaltyCustomer
        }}>
            {children}
        </DataContext.Provider>
    );
};

export const useData = () => {
    const context = useContext(DataContext);
    if (context === undefined) {
        throw new Error('useData must be used within a DataProvider');
    }
    return context;
};
